import React from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { useAppDispatch } from '@/store';
import { createDepositIntent } from '@/store/slices/wallet';
import { stripeLocale } from '@/utils/stripe-locale';

import { Button, Icon, Text } from '../../../../components';
import { cleanAmountInput, currencySymbol, formatAmountDisplay, formatAmountWithCode } from '../../../../utils/money';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { getStripe, useStripeAppearance } from '../../../configurator-v3/data/payments';

export type DepositModalProps = {
  /** Wallet currency code (e.g. "USD"), drives the Stripe PaymentIntent currency. */
  currency: string;
  onClose: () => void;
  /** Fired once a deposit clears (after the Stripe charge is confirmed), so the caller can
      refetch the wallet to reflect the new server balance. */
  onSuccess: () => void;
};

/** Smallest top-up we accept; also keeps every charge above Stripe's per-currency minimum. */
const MIN_DEPOSIT = 1;
/** Quick-pick amounts shown under the input. */
const PRESETS = [100, 500, 1000, 5000];

/** Fade-out duration before the body/footer is swapped to the next screen. Keep in sync with the
    `.deposit-screen` transition in wallet.scss. */
const SCREEN_FADE_MS = 150;

/** The five screens the modal moves through. They all live inside ONE Overlay so moving between
    them grows + cross-fades in place (no modal close/reopen flicker):
    amount → starting (creating the intent) → form (Stripe) → success, with error as the
    recoverable branch off `starting`. */
type Screen = 'amount' | 'starting' | 'error' | 'form' | 'success';

/** Cross-fade when `screen` changes: fade the current content out, swap to the new screen, fade it
    back in. The Overlay animates its own height around the swap (`animateResize`), so the modal
    grows/shrinks smoothly while the content fades. */
function useScreenFade(screen: Screen): { rendered: Screen; shown: boolean } {
  const [rendered, setRendered] = React.useState<Screen>(screen);
  const [shown, setShown] = React.useState(true);
  const timer = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (screen === rendered) return;
    setShown(false); // fade the current screen out
    timer.current = window.setTimeout(() => {
      setRendered(screen); // swap content (the Overlay re-measures + animates its height)
      setShown(true); // fade the new screen in
    }, SCREEN_FADE_MS);
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [screen, rendered]);
  return { rendered, shown };
}

/** The deposit amount being paid + an "edit amount" affordance (shown on the pay screens).
 *  `amountLabel` is preformatted in the wallet's currency by the parent. */
function DepositSummary({ amountLabel, onEdit }: { amountLabel: string; onEdit: () => void }): React.ReactElement {
  return (
    <div className="deposit-summary">
      <div className="deposit-summary-head">
        <Text tag="span" className="deposit-summary-label" text="Adding to your wallet" />
        <button type="button" className="deposit-edit-link" onClick={onEdit}>
          <Text text="Edit amount" />
        </button>
      </div>
      <span className="deposit-summary-amount">{amountLabel}</span>
    </div>
  );
}

/** The Stripe Payment Element + the confirm logic, mounted inside <Elements> bound to the deposit
    PaymentIntent's clientSecret (intent-first, configurator-v3 wiring). Because Elements is bound
    to the real intent it uses the backend's `automatic_payment_methods` as-is (no client/BE
    conflict) and reads the intent's real setup_future_usage, so wallet methods confirm without a
    mismatch. The accordion lists the available methods with CARD expanded by default. It registers
    its `confirm` into `confirmRef` so the modal's footer Pay button (rendered outside <Elements>)
    can trigger it, and reports paying/error back up to the modal. */
function StripeForm(p: {
  clientSecret: string;
  confirmRef: React.MutableRefObject<(() => void) | null>;
  setPaying: (value: boolean) => void;
  setError: (value: string | null) => void;
  onPaid: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { clientSecret, confirmRef, setPaying, setError, onPaid } = p;

  React.useEffect(() => {
    confirmRef.current = async (): Promise<void> => {
      if (!stripe || !elements) return;
      setPaying(true);
      setError(null);
      const fail = (message: string | undefined): void => {
        setError(message ?? t('Payment failed. Please try again.'));
        setPaying(false);
      };
      // Validate + collect the element first, then confirm against the bound intent.
      const submitResult = await elements.submit();
      if (submitResult.error) {
        fail(submitResult.error.message);
        return;
      }
      const result = await stripe.confirmPayment({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.origin + window.location.pathname },
      });
      if (result.error) {
        fail(result.error.message);
        return;
      }
      // Charged. Clear the in-flight state BEFORE handing off: it is what tells the Overlay the
      // dialog is busy, and a busy dialog refuses every dismissal. Left set, the success screen
      // would render with its Done button, its X, Escape, the backdrop and the drag all inert,
      // freezing the studio behind a full-viewport backdrop the instant the card went through.
      setPaying(false);
      onPaid();
    };
    return () => {
      confirmRef.current = null;
    };
  }, [stripe, elements, clientSecret, confirmRef, setPaying, setError, onPaid, t]);

  // Accordion of the intent's automatic payment methods, with the first (card) expanded by default.
  return <PaymentElement options={{ layout: { type: 'accordion', defaultCollapsed: false } }} />;
}

/**
 * Wallet top-up modal. Reuses the configurator's Stripe integration (publishable key + appearance
 * + Elements). Everything lives inside ONE standard Overlay (centred on desktop, bottom sheet on
 * mobile): enter an amount, create the deposit PaymentIntent (intent-first, see {@link StripeForm}),
 * pay with Stripe, then a success confirmation. Moving between screens grows + cross-fades the
 * modal in place rather than closing and reopening. On success the caller refetches the wallet.
 */
export const DepositModal = ({ currency, onClose, onSuccess }: DepositModalProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  // Dismiss through the overlay so the dialog slides out; wired straight to onClose it
  // would unmount on the spot and blink away.
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  const dispatch = useAppDispatch();
  // The deposit tops up the MAIN wallet, so every figure reads in that wallet's currency (the
  // selected casino's currency, resolved by the parent) rather than a hardcoded "$".
  const currencyCode = currency || 'USD';
  /* Once an intent exists, the money on screen is the money Stripe will take, so it reads in
   *  the currency that intent was MINTED in and not in whatever the prop says now. The two can
   *  part company: the intent is minted once, while `currency` follows the selected brand,
   *  which the owner can change from the top bar with this dialog still open. Re-labelling a
   *  bound intent would show one price and charge another. */
  const [intentCurrency, setIntentCurrency] = React.useState<string | null>(null);
  const chargeCurrency = intentCurrency ?? currencyCode;
  const money = (value: number): string => `${currencySymbol(chargeCurrency)}${formatAmountWithCode(value, chargeCurrency)}`;
  // Stripe Elements appearance, resolved to the active studio theme (re-themes on a light/dark switch).
  const appearance = useStripeAppearance();

  const [screen, setScreen] = React.useState<Screen>('amount');
  const [amountInput, setAmountInput] = React.useState('');
  const [amountFocused, setAmountFocused] = React.useState(false);
  const [paidAmount, setPaidAmount] = React.useState(0);

  const [clientSecret, setClientSecret] = React.useState<string | null>(null);
  const [paying, setPaying] = React.useState(false);
  const [payError, setPayError] = React.useState<string | null>(null);
  const [startError, setStartError] = React.useState<string | null>(null);
  // Set by StripeForm (inside <Elements>) so the footer Pay button can trigger the confirm.
  const confirmRef = React.useRef<(() => void) | null>(null);

  const amountValue = Number(amountInput);
  const amountEntered = amountInput.trim() !== '';
  const amountValid = amountEntered && Number.isFinite(amountValue) && amountValue >= MIN_DEPOSIT;
  const amountError =
    amountEntered && !amountValid
      ? t('Enter an amount of at least {{amount}}', { amount: money(MIN_DEPOSIT) })
      : null;

  const onChangeAmount = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setAmountInput(cleanAmountInput(e.currentTarget.value));
  }, []);
  // Commit the amount → create the deposit intent (`starting`) → Stripe form.
  const onContinue = React.useCallback(() => {
    if (amountValid) setScreen('starting');
  }, [amountValid]);
  // Back to edit the amount. Keep any in-flight clientSecret mounted so the form fades out cleanly;
  // pressing Continue again mints a fresh intent for the (possibly new) amount.
  const onEdit = React.useCallback(() => {
    setPayError(null);
    setStartError(null);
    setScreen('amount');
  }, []);
  const onRetry = React.useCallback(() => setScreen('starting'), []);
  const onPaid = React.useCallback(() => {
    setPaidAmount(amountValue);
    onSuccess();
    setScreen('success');
  }, [amountValue, onSuccess]);

  // Create the PaymentIntent up front (intent-first, mirroring configurator-v3) when entering the
  // `starting` screen: the Payment Element binds to the real intent, so it uses the backend's
  // automatic_payment_methods as-is (no client/BE conflict) and confirms without a
  // setup_future_usage mismatch. A fresh intent per `starting` entry (mints a pending invoice,
  // filtered from the Invoices tab); `t` is intentionally NOT a dep (it would mint another invoice
  // on a language switch).
  React.useEffect(() => {
    if (screen !== 'starting') return;
    let cancelled = false;
    setPayError(null);
    setStartError(null);
    setClientSecret(null);
    void (async () => {
      try {
        const cs = (await dispatch(createDepositIntent({ amount: amountValue, currency: currencyCode })).unwrap())
          .clientSecret;
        if (!cancelled) {
          setClientSecret(cs);
          setScreen('form');
        }
      } catch (e) {
        if (!cancelled) {
          setStartError((e as { error?: string } | undefined)?.error ?? t('Could not start checkout. Please try again.'));
          setScreen('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, amountValue, currency, dispatch]);

  const { rendered, shown } = useScreenFade(screen);

  // ---- body + footer for the screen being rendered (cross-faded together) ----
  let body: React.ReactElement;
  let footer: React.ReactElement;
  switch (rendered) {
    case 'success':
      body = (
        <div className="deposit-success">
          <span className="deposit-success-icon" aria-hidden="true">
            <Icon name="check" size={28} />
          </span>
          <Text tag="span" className="deposit-success-title" text="Deposit successful" />
          <Text
            tag="span"
            className="deposit-success-body"
            text="{{amount}} was added to your wallet balance."
            placeholders={{ amount: money(paidAmount) }}
          />
          <Text
            tag="span"
            className="deposit-success-note"
            text="It may take a few moments for the payment to be fully processed and your balance to update."
          />
        </div>
      );
      footer = <Button variant="primary" fullWidth label={t('Done')} onClick={dismiss} />;
      break;

    case 'starting':
      body = (
        <div className="deposit-form">
          <DepositSummary amountLabel={money(amountValue)} onEdit={onEdit} />
          <Text tag="span" className="deposit-intro" text="Starting secure checkout…" />
        </div>
      );
      footer = (
        <Button
          variant="primary"
          icon="wallet"
          fullWidth
          disabled
          label={t('Pay {{amount}}', { amount: money(amountValue) })}
        />
      );
      break;

    case 'error':
      body = (
        <div className="deposit-form">
          <DepositSummary amountLabel={money(amountValue)} onEdit={onEdit} />
          <span className="deposit-error" role="alert">
            {startError}
          </span>
        </div>
      );
      footer = <Button variant="primary" fullWidth label={t('Try again')} onClick={onRetry} />;
      break;

    case 'form':
      body = (
        <div className="deposit-form">
          <DepositSummary amountLabel={money(amountValue)} onEdit={onEdit} />
          <div className="deposit-stripe">
            {clientSecret ? (
              <Elements
                // `locale` is NOT mutable after mount, unlike `appearance` — keying on it
                // remounts Elements on a language switch instead of leaving Stripe stuck in the
                // language the modal opened in.
                key={stripeLocale(i18n.language)}
                stripe={getStripe()}
                options={{
                  clientSecret,
                  appearance,
                  // Stripe renders its own copy (field labels, validation errors) and defaults
                  // to the BROWSER's language rather than ours unless told explicitly.
                  locale: stripeLocale(i18n.language),
                }}
              >
                <StripeForm
                  clientSecret={clientSecret}
                  confirmRef={confirmRef}
                  setPaying={setPaying}
                  setError={setPayError}
                  onPaid={onPaid}
                />
              </Elements>
            ) : null}
          </div>
          {payError ? (
            <span className="deposit-error" role="alert">
              {payError}
            </span>
          ) : null}
        </div>
      );
      footer = (
        <Button
          variant="primary"
          icon="wallet"
          fullWidth
          disabled={paying}
          label={paying ? t('Processing…') : t('Pay {{amount}}', { amount: money(amountValue) })}
          onClick={() => void confirmRef.current?.()}
        />
      );
      break;

    case 'amount':
    default:
      body = (
        <div className="deposit-form">
          <Text tag="span" className="deposit-intro" text="Add money to your wallet balance with your card." />
          <div className="payout-field">
            <div className="payout-field-head">
              <label className="payout-field-label" htmlFor="deposit-amount">
                {t('Amount')}
              </label>
            </div>
            <div className={`payout-amount-input ${amountError ? 'has-error' : ''}`}>
              <span className="payout-amount-prefix">{currencySymbol(currencyCode)}</span>
              <input
                id="deposit-amount"
                className="payout-amount-control"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0"
                value={amountFocused ? amountInput : formatAmountDisplay(amountInput)}
                onChange={onChangeAmount}
                onFocus={() => setAmountFocused(true)}
                onBlur={() => setAmountFocused(false)}
                aria-invalid={!!amountError}
                aria-describedby={amountError ? 'deposit-amount-error' : undefined}
              />
            </div>
            {amountError && (
              <span id="deposit-amount-error" className="payout-amount-error" role="alert">
                {amountError}
              </span>
            )}
          </div>

          <div className="deposit-presets">
            {PRESETS.map((v) => (
              <button
                key={v}
                type="button"
                className={`deposit-preset ${amountValue === v ? 'is-selected' : ''}`}
                onClick={() => setAmountInput(String(v))}
              >
                {money(v)}
              </button>
            ))}
          </div>
        </div>
      );
      footer = (
        <Button variant="primary" fullWidth disabled={!amountValid} label={t('Continue')} onClick={onContinue} />
      );
      break;
  }

  const fadeClass = `deposit-screen ${shown ? 'is-in' : 'is-out'}`;

  return (
    <Sheet
      title={t('Deposit funds')}
      onClose={onClose}
      closeHandle={closeHandle}
      // A card confirm is in flight, and a 3DS challenge may be on screen inside it. Dismissing
      // now abandons a charge that still completes at Stripe: the money leaves and the app never
      // learns, so there is no success screen and nothing watches for the credit.
      busy={paying}
      animateResize
      footer={<div className={fadeClass}>{footer}</div>}
    >
      <div className={fadeClass}>{body}</div>
    </Sheet>
  );
};

export default DepositModal;
